import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { InvalidParamError } from '@dynamic-labs-sdk/client';
import {
  type TransferAmountParams,
  parseAmountToSmallestUnit,
} from '@dynamic-labs-sdk/client/core';
import { Transaction } from '@mysten/sui/transactions';

import { getSuiClient } from '../../getSuiClient';
import { isSuiWalletAccount } from '../../isSuiWalletAccount';
import { signAndExecuteTransaction } from '../../signAndExecuteTransaction';

const SUI_DECIMALS = 9;

/**
 * Transfers native SUI or a SUI token from one wallet to another.
 *
 * For native SUI, converts the human-readable amount string to MIST (9 decimals).
 * For tokens, fetches the user's coins of that type and splits from them.
 */
export const suiTransferAmount = async (
  { amount, recipient, token, walletAccount }: TransferAmountParams,
  client: DynamicClient
): Promise<{ transactionHash: string }> => {
  if (!isSuiWalletAccount(walletAccount)) {
    throw new InvalidParamError('Wallet account is not a SUI wallet account');
  }

  const transaction = new Transaction();

  if (token?.address) {
    const suiClient = await getSuiClient({ walletAccount }, client);

    const { data: coins } = await suiClient.getCoins({
      coinType: token.address,
      owner: walletAccount.address,
    });

    if (coins.length === 0) {
      throw new InvalidParamError('Token not found');
    }

    const smallestUnit = parseAmountToSmallestUnit({
      amount,
      decimals: token.decimals,
    });

    const [coin] = transaction.splitCoins(coins[0].coinObjectId, [
      smallestUnit,
    ]);
    transaction.transferObjects([coin], recipient);
  } else {
    const mist = parseAmountToSmallestUnit({
      amount,
      decimals: SUI_DECIMALS,
    });

    const [coin] = transaction.splitCoins(transaction.gas, [mist]);
    transaction.transferObjects([coin], recipient);
  }

  const result = await signAndExecuteTransaction(
    { transaction, walletAccount },
    client
  );

  return { transactionHash: result.digest };
};
